import { createApiCall } from './index'

export interface ConnectShopifyRequest {
  shop_domain: string
  origin?: 'integrations' | 'stepper'
}

export interface ConnectShopifyResponse {
  auth_url: string
  state: string
  connection_id: string
}

export interface CompleteOAuthRequest {
  code: string
  state: string
  shop: string
}

export interface CompleteOAuthResponse {
  message: string
  connection_id: string
}


export interface ImportProductsRequest {
  limit?: number
  offset?: number
}

export interface ImportProductsResponse {
  products: Array<{
    id: string
    title: string
    sku: string | null
    description: string
    price: number
    images: string[]
    created_at: string
    updated_at: string
    imported?: boolean
    category_id?: string | null
    /** Listing URL on the brand's storefront, resolved server-side by the provider. */
    product_url?: string | null
  }>
  total: number
  has_more: boolean
}

export interface ConnectionStatusResponse {
  connected: boolean
  message: string
  provider: string
  company_id: string
}

export interface CreateWidgetPageRequest {
  title?: string
  handle?: string
  isPublished?: boolean
  environment?: string
  customScriptUrl?: string
}

export interface CreateWidgetPageResponse {
  page_id: string
  title: string
  handle: string
  url: string
  is_published: boolean
  widget_content: string
  installation_id: string
  company_id: string
}

export interface DeleteWidgetPageRequest {
  page_id: string
}

export interface DeleteWidgetPageResponse {
  message: string
  deleted_page_id: string
  handle: string
}

export interface ListWidgetPagesResponse {
  pages: Array<{
    page_id: string
    title: string
    handle: string
    url: string
    created_at: string
    installation_id: string
  }>
}

export const shopifyApi = {
  async connectStore(request: ConnectShopifyRequest): Promise<ConnectShopifyResponse> {
    const response = await createApiCall({
      method: 'POST',
      url: '/integrations/shopify/connect',
      data: request,
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to connect to Shopify')
    }

    return response.data
  },

  async completeOAuth(request: CompleteOAuthRequest): Promise<CompleteOAuthResponse> {
    const response = await createApiCall({
      method: 'GET',
      url: '/integrations/shopify/callback',
      params: request,
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to complete OAuth')
    }

    return response.data
  },

  async disconnectStore(): Promise<void> {
    const response = await createApiCall({
      method: 'DELETE',
      url: '/integrations/shopify/disconnect',
    })
    
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to disconnect from Shopify')
    }
  },


  async importProducts(request: ImportProductsRequest = {}): Promise<ImportProductsResponse> {
    let response
    try {
      response = await createApiCall({
        method: 'GET',
        url: '/integrations/shopify/import/products',
        params: request,
      })
    } catch (error: any) {
      // Local QA fallback: use seeded local products when Shopify integration is unavailable.
      //
      // This whole block used to hang off `if (response.status !== 200)`, which stopped running
      // once `createApiCall` began rejecting (VRG-386) — the failure escaped at the `await`
      // above and the fallback became unreachable, so local QA with no Shopify connection saw
      // the import fail outright rather than falling back to seeded products.
      if (import.meta.env.MODE === 'development') {
        const localProductsResponse = await createApiCall({
          method: 'GET',
          url: '/products/admin/company',
          params: {
            limit: request.limit ?? 100,
            offset: request.offset ?? 0,
          },
        })

        const localData = localProductsResponse.data as {
          data?: Array<{
            id: string
            title: string
            sku: string | null
            description?: string | null
            images?: string[] | null
            created_at?: string
            updated_at?: string | null
          }>
          total?: number
          limit?: number
          offset?: number
        }
        const products = (localData.data || []).map((product) => ({
          id: product.id,
          title: product.title,
          sku: product.sku ?? null,
          description: product.description ?? '',
          price: 0,
          images: product.images || [],
          created_at: product.created_at || new Date().toISOString(),
          updated_at: product.updated_at || new Date().toISOString(),
        }))

        return {
          products,
          total: localData.total ?? products.length,
          has_more:
            (localData.offset ?? 0) + (localData.limit ?? products.length) <
            (localData.total ?? products.length),
        }
      }

      throw error
    }

    return response.data
  },

  async getConnectionStatus(): Promise<ConnectionStatusResponse> {
    const response = await createApiCall({
      method: 'GET',
      url: '/integrations/shopify/connection/status',
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to get connection status')
    }

    return response.data
  },

  async createWidgetPage(request: CreateWidgetPageRequest = {}): Promise<CreateWidgetPageResponse> {
    const response = await createApiCall({
      method: 'POST',
      url: '/integrations/shopify/create-widget-page',
      data: request,
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to create widget page')
    }

    return response.data
  },

  async deleteWidgetPage(request: DeleteWidgetPageRequest): Promise<DeleteWidgetPageResponse> {
    const response = await createApiCall({
      method: 'DELETE',
      url: '/integrations/shopify/delete-widget-page',
      data: request,
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to delete widget page')
    }

    return response.data
  },

  async listWidgetPages(): Promise<ListWidgetPagesResponse> {
    const response = await createApiCall({
      method: 'GET',
      url: '/integrations/shopify/widget-pages',
    })

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to list widget pages')
    }

    return response.data
  },
}